(function () {
  if (window.HcCartCustomPreview) return;

  const previewCache = new Map();
  const imageCache = new Map();
  const loadedFonts = new Set();

  const EYE_ICON_SVG = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="currentColor">'
    + '<path d="M9.5235 4.79973C6.76257 4.92905 4.08307 6.62063 1.1722 9.66543C0.993412 9.85244 0.993412 10.1474 1.1722 10.3344C4.08307 13.3793 6.76258 15.0709 9.52351 15.2003C12.2733 15.3291 15.2667 13.9138 18.8217 10.3399C19.0086 10.152 19.0086 9.84814 18.8217 9.6602C15.2667 6.0863 12.2733 4.67093 9.5235 4.79973ZM9.47509 3.7592C12.6521 3.61039 15.9149 5.26347 19.5564 8.92433C20.1479 9.5189 20.1479 10.4812 19.5564 11.0758C15.9149 14.7366 12.6521 16.3897 9.47508 16.2408C6.30917 16.0924 3.3912 14.1603 0.42305 11.0555C-0.141017 10.4655 -0.141017 9.53435 0.423051 8.94433C3.3912 5.8396 6.30918 3.90749 9.47509 3.7592Z"/>'
    + '<path d="M13.5807 10.0002C13.5807 11.9741 11.9742 13.5586 10.012 13.5586C8.04979 13.5586 6.44327 11.9741 6.44327 10.0002C6.44327 8.02617 8.04979 6.44176 10.012 6.44176C11.9742 6.44176 13.5807 8.02617 13.5807 10.0002ZM10.012 12.5169C11.4096 12.5169 12.5426 11.3901 12.5426 10.0002C12.5426 8.6102 11.4096 7.48342 10.012 7.48342C8.61438 7.48342 7.48138 8.6102 7.48138 10.0002C7.48138 11.3901 8.61438 12.5169 10.012 12.5169Z"/>'
    + '</svg>';

  const CLOSE_ICON_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m6 6 12 12M18 6 6 18"></path></svg>';

  function decodeBase64Json(value) {
    const binary = window.atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  function readPayload(container) {
    const script = container.querySelector('[data-hc-custom-preview-payload]');
    if (!script) return null;

    let envelope;
    try {
      envelope = JSON.parse(script.textContent || '{}');
    } catch (error) {
      return null;
    }

    const chunks = Array.isArray(envelope.chunks) ? envelope.chunks.filter(Boolean) : [];
    if (!chunks.length) return null;

    const encodedPayload = chunks.join('');
    const cacheKey = `${envelope.encoding || 'base64-json'}:${encodedPayload}`;
    if (previewCache.has(cacheKey)) return previewCache.get(cacheKey);

    try {
      const payloadText = envelope.encoding === 'base64-json'
        ? decodeBase64Json(encodedPayload)
        : encodedPayload;
      const payload = JSON.parse(payloadText);
      previewCache.set(cacheKey, payload);
      return payload;
    } catch (error) {
      return null;
    }
  }

  function getImageUrl(source) {
    if (!source) return '';
    if (typeof source === 'string') return source;

    return source.imageUrl
      || source.url
      || source.src
      || source.previewImage?.imageUrl
      || source.previewImage?.url
      || source.preview_image?.imageUrl
      || source.preview_image?.url
      || '';
  }

  function getLayerList(model) {
    if (!model || typeof model !== 'object') return [];

    const layerSources = [
      model.layers,
      model.images,
      model.items,
      model.overlays,
      model.previewLayers
    ];

    return layerSources.find(Array.isArray) || [];
  }

  function ratioRect(rect, size) {
    return {
      x: Number(rect?.x || 0) * size,
      y: Number(rect?.y || 0) * size,
      width: Number(rect?.width || 0) * size,
      height: Number(rect?.height || 0) * size
    };
  }

  function loadPreviewImage(url) {
    const key = String(url || '');
    if (!key) return Promise.reject(new Error('Missing preview image URL.'));
    if (!imageCache.has(key)) {
      imageCache.set(key, new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = key;
      }).catch((error) => {
        imageCache.delete(key);
        throw error;
      }));
    }

    return imageCache.get(key);
  }

  function ensurePreviewFont(layer) {
    const fontFamily = String(layer?.fontFamily || '').trim();
    const fontUrl = String(layer?.fontUrl || '').trim();
    const fontType = String(layer?.fontType || '').trim();
    const key = `${fontFamily}|${fontUrl}|${fontType}`;
    if (!fontFamily || loadedFonts.has(key)) return Promise.resolve();
    loadedFonts.add(key);

    if (fontUrl && 'FontFace' in window) {
      const face = new FontFace(fontFamily, `url("${fontUrl.replace(/"/g, '\\"')}")`);
      return face.load().then((loadedFace) => {
        document.fonts.add(loadedFace);
      }).catch(() => {});
    }

    if (/googlefont/i.test(fontType)) {
      const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily).replace(/%20/g, '+')}&display=swap`;
      if (![...document.querySelectorAll('link[href]')].some((link) => link.href === href)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      }
    }

    return Promise.resolve();
  }

  function isCanvasPreviewModel(model) {
    return getLayerList(model).some((layer) => ['image', 'clipped-image', 'text'].includes(layer?.type));
  }

  function hasRenderableCustomLayers(model) {
    const layers = getLayerList(model);
    if (!layers.length) return false;

    let imageLayerCount = 0;

    for (const layer of layers) {
      if (layer?.type === 'text' && String(layer.text || '').trim()) return true;
      if (layer?.type === 'clipped-image' && layer.src) return true;
      if (layer?.type === 'image') imageLayerCount += 1;
    }

    return imageLayerCount >= 2;
  }

  async function drawPreviewCanvas(model, size) {
    const layers = getLayerList(model);
    if (!layers.length) return null;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (!context) return null;

    context.fillStyle = model.background || '#ffffff';
    context.fillRect(0, 0, size, size);

    await Promise.all(layers.filter((layer) => layer?.type === 'text').map(ensurePreviewFont));

    for (const layer of layers) {
      if (layer?.type === 'image') {
        try {
          const image = await loadPreviewImage(layer.src);
          const rect = ratioRect(layer.rect, size);
          context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
        } catch (error) {}
        continue;
      }

      if (layer?.type === 'clipped-image') {
        try {
          const image = await loadPreviewImage(layer.src);
          const clipRect = ratioRect(layer.clipRect, size);
          const imageRect = ratioRect(layer.imageRect, size);
          context.save();
          context.beginPath();
          context.rect(clipRect.x, clipRect.y, clipRect.width, clipRect.height);
          context.clip();
          context.drawImage(image, imageRect.x, imageRect.y, imageRect.width, imageRect.height);
          context.restore();
        } catch (error) {}
        continue;
      }

      if (layer?.type === 'text') {
        const rect = ratioRect(layer.rect, size);
        const lines = String(layer.text || '').split(/\r?\n/);
        const fontSize = Math.max(10, (Number(layer.fontSizeRatio) || 0.05) * size);
        const lineHeight = Math.max(fontSize * 1.18, (Number(layer.lineHeightRatio) || 0.06) * size);
        const fontFamily = String(layer.fontFamily || 'Arial').replace(/"/g, '\\"');

        context.save();
        context.fillStyle = layer.color || '#000000';
        context.font = `${fontSize}px "${fontFamily}", Arial, sans-serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';

        if (layer.singleLine) {
          context.fillText(lines.join(' ').replace(/\s+/g, ' '), rect.x + rect.width / 2, rect.y + rect.height / 2, rect.width);
        } else {
          lines.forEach((line, index) => {
            context.fillText(line, rect.x + rect.width / 2, rect.y + rect.height / 2 + (index - (lines.length - 1) / 2) * lineHeight, rect.width);
          });
        }

        context.restore();
      }
    }

    return canvas;
  }

  function createZoomButton(container) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hc-cart-custom-preview__zoom';
    button.setAttribute('data-hc-preview-zoom', '');
    button.setAttribute('aria-label', container.dataset.hcZoomLabel || '');
    button.innerHTML = EYE_ICON_SVG;
    return button;
  }

  async function renderCanvasPreview(container, model) {
    const canvas = await drawPreviewCanvas(model, 320);
    if (!canvas) return false;

    canvas.className = 'hc-cart-custom-preview__canvas';

    const frame = document.createElement('div');
    frame.className = 'hc-cart-custom-preview__frame';
    frame.appendChild(canvas);
    container.replaceChildren(frame, createZoomButton(container));
    container.__hcPreviewModel = model;
    return true;
  }

  function createImage(source, index) {
    const imageUrl = getImageUrl(source);
    if (!imageUrl) return null;

    const image = document.createElement('img');
    const x = Number(source.x ?? source.left ?? 0);
    const y = Number(source.y ?? source.top ?? 0);
    const width = source.width ?? source.w;
    const height = source.height ?? source.h;

    image.src = imageUrl;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.className = 'hc-cart-custom-preview__image';
    image.style.zIndex = String(Number(source.zIndex ?? source.z ?? index) || index);

    if (Number.isFinite(x)) image.style.left = `${x}%`;
    if (Number.isFinite(y)) image.style.top = `${y}%`;
    if (width) image.style.width = typeof width === 'number' ? `${width}%` : String(width);
    if (height) image.style.height = typeof height === 'number' ? `${height}%` : String(height);

    return image;
  }

  function renderLegacyPreview(container, model) {
    const frame = document.createElement('div');
    frame.className = 'hc-cart-custom-preview__frame';

    const baseImage = createImage(
      model.baseImage || model.backgroundImage || model.canvasImage || model.image,
      0
    );
    if (baseImage) frame.appendChild(baseImage);

    getLayerList(model).forEach((layer, index) => {
      const layerImage = createImage(layer, index + 1);
      if (layerImage) frame.appendChild(layerImage);
    });

    if (!frame.children.length) return false;

    container.replaceChildren(frame);
    return true;
  }

  function applyFallbackState(container) {
    if (container.querySelector('[data-hc-custom-preview-fallback]')) {
      container.classList.add('is-fallback');
    } else {
      container.classList.add('is-empty');
    }
  }

  let lightboxElement = null;
  const lightboxState = {
    previousFocus: null,
    previousOverflow: '',
    overflowLocked: false,
    drawerWasTrapped: false,
    keydownHandler: null,
    closeTimer: 0,
    opening: false
  };

  function ensureLightbox() {
    if (lightboxElement) return lightboxElement;

    lightboxElement = document.createElement('div');
    lightboxElement.className = 'hc-cart-preview-lightbox';
    lightboxElement.hidden = true;
    lightboxElement.setAttribute('aria-hidden', 'true');
    lightboxElement.setAttribute('role', 'dialog');
    lightboxElement.setAttribute('aria-modal', 'true');
    lightboxElement.innerHTML = '<div class="hc-cart-preview-lightbox__backdrop" data-hc-lightbox-close></div>'
      + '<div class="hc-cart-preview-lightbox__dialog" tabindex="-1">'
      + `<button type="button" class="hc-cart-preview-lightbox__close" data-hc-lightbox-close>${CLOSE_ICON_SVG}</button>`
      + '<div class="hc-cart-preview-lightbox__body"></div>'
      + '</div>';

    lightboxElement.addEventListener('click', (event) => {
      if (event.target.closest('[data-hc-lightbox-close]')) closePreviewLightbox();
    });

    document.body.appendChild(lightboxElement);
    return lightboxElement;
  }

  async function openPreviewLightbox(container) {
    const model = container && container.__hcPreviewModel;
    if (!model || lightboxState.opening) return;
    if (lightboxElement && !lightboxElement.hidden && !lightboxElement.classList.contains('is-closing')) return;

    lightboxState.opening = true;

    try {
      const element = ensureLightbox();

      let canvas = null;
      try {
        canvas = await drawPreviewCanvas(model, 1024);
      } catch (error) {
        canvas = null;
      }
      if (!canvas) return;

      canvas.className = 'hc-cart-preview-lightbox__canvas';

      window.clearTimeout(lightboxState.closeTimer);
      element.classList.remove('is-closing');

      const previewLabel = container.getAttribute('aria-label');
      if (previewLabel) element.setAttribute('aria-label', previewLabel);

      const closeButton = element.querySelector('.hc-cart-preview-lightbox__close');
      if (closeButton && container.dataset.hcLightboxCloseLabel) {
        closeButton.setAttribute('aria-label', container.dataset.hcLightboxCloseLabel);
      }

      element.querySelector('.hc-cart-preview-lightbox__body').replaceChildren(canvas);

      lightboxState.previousFocus = document.activeElement;
      lightboxState.drawerWasTrapped = Boolean(document.querySelector('cart-drawer.active'));
      if (lightboxState.drawerWasTrapped && typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      if (!document.body.classList.contains('prea-cart-drawer-open')) {
        lightboxState.previousOverflow = document.body.style.overflow;
        lightboxState.overflowLocked = true;
        document.body.style.overflow = 'hidden';
      } else {
        lightboxState.overflowLocked = false;
      }

      lightboxState.keydownHandler = (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        // Swallow the matching keyup so the cart drawer's own Escape handler
        // (bound on keyup) does not close the drawer underneath the lightbox.
        document.addEventListener('keyup', (keyupEvent) => {
          if (keyupEvent.key === 'Escape') keyupEvent.stopPropagation();
        }, { capture: true, once: true });
        closePreviewLightbox();
      };
      document.addEventListener('keydown', lightboxState.keydownHandler);

      element.hidden = false;
      element.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
        element.classList.add('is-open');
        element.querySelector('.hc-cart-preview-lightbox__dialog')?.focus({ preventScroll: true });
      });
    } finally {
      lightboxState.opening = false;
    }
  }

  function closePreviewLightbox() {
    if (!lightboxElement || lightboxElement.hidden) return;

    lightboxElement.classList.remove('is-open');
    lightboxElement.classList.add('is-closing');
    lightboxElement.setAttribute('aria-hidden', 'true');

    if (lightboxState.keydownHandler) {
      document.removeEventListener('keydown', lightboxState.keydownHandler);
      lightboxState.keydownHandler = null;
    }

    if (lightboxState.overflowLocked) {
      document.body.style.overflow = lightboxState.previousOverflow;
      lightboxState.overflowLocked = false;
    }

    lightboxState.closeTimer = window.setTimeout(() => {
      lightboxElement.hidden = true;
      lightboxElement.classList.remove('is-closing');
    }, 270);

    const previousFocus = lightboxState.previousFocus;
    lightboxState.previousFocus = null;

    const activeDrawer = document.querySelector('cart-drawer.active');
    if (activeDrawer && lightboxState.drawerWasTrapped && typeof trapFocus === 'function') {
      const drawerContainer = document.getElementById('CartDrawer') || activeDrawer;
      const focusTarget = previousFocus instanceof HTMLElement && previousFocus.isConnected && drawerContainer.contains(previousFocus)
        ? previousFocus
        : (activeDrawer.querySelector('.drawer__inner') || drawerContainer);
      trapFocus(drawerContainer, focusTarget);
    } else if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
      previousFocus.focus({ preventScroll: true });
    }

    lightboxState.drawerWasTrapped = false;
  }

  document.addEventListener('click', (event) => {
    const zoomButton = event.target.closest && event.target.closest('[data-hc-preview-zoom]');
    if (!zoomButton) return;

    event.preventDefault();
    openPreviewLightbox(zoomButton.closest('[data-hc-custom-preview]'));
  });

  function hydrate(root = document) {
    const containers = root.querySelectorAll('[data-hc-custom-preview]:not(.is-hydrated):not(.is-loading)');

    containers.forEach(async (container) => {
      container.classList.add('is-loading');

      try {
        const payload = readPayload(container);
        const model = payload ? (payload.previewModel || payload.preview || payload.model) : null;

        if (!model) {
          applyFallbackState(container);
          return;
        }

        if (isCanvasPreviewModel(model)) {
          if (!hasRenderableCustomLayers(model)) {
            applyFallbackState(container);
            return;
          }

          if (await renderCanvasPreview(container, model)) return;
          applyFallbackState(container);
          return;
        }

        if (renderLegacyPreview(container, model)) return;
        applyFallbackState(container);
      } catch (error) {
        applyFallbackState(container);
      } finally {
        container.classList.remove('is-loading');
        container.classList.add('is-hydrated');
      }
    });
  }

  window.HcCartCustomPreview = { hydrate };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrate(), { once: true });
  } else {
    hydrate();
  }
})();
